// ArticleDetailPage.js

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { articlesService } from '../Services/articlesService';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Calendar, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ArticleDetailPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await articlesService.getArticleById(articleId);
      setArticle(data);
    } catch (error) {
      console.error('❌ Erreur chargement article:', error);
      toast.error('Article introuvable');
      navigate('/daily-help');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!article) return null;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Bouton retour */}
        <Button
          onClick={() => navigate('/daily-help')}
          variant="outline"
          className="mb-6 flex items-center gap-2 dark:border-gray-700 dark:text-gray-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Button>

        {/* Article */}
        <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Image */}
          {article.images?.[0] && (
            <img src={article.images[0]} alt={article.title} className="w-full h-96 object-cover" />
          )}

          {/* Contenu */}
          <div className="p-4 lg:p-6">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags?.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Titre */}
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {article.title}
            </h1>

            {/* Métadonnées */}
            <div className="flex flex-wrap gap-6 mb-6 text-gray-600 dark:text-gray-400">
              {article.budget && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  <span>{article.budget}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span
                  className={`px-2 py-1 rounded ${
                    article.difficulty === 'Facile'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : article.difficulty === 'Moyen'
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}
                >
                  {article.difficulty}
                </span>
              </div>
              {article.createdAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>
                    {new Date(article.createdAt.seconds * 1000).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
              {article.description}
            </p>

            {/* Contenu de l'article */}
            <div
              className="article-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  article.content
                    ?.replace(/&nbsp;/g, ' ')
                    ?.replace(/&#39;/g, "'")
                    ?.replace(
                      /<a href="(https:\/\/www\.youtube\.com\/embed\/[^"]+)"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="$1" frameborder="0" allowfullscreen></iframe></div>'
                    )
                    ?.replace(
                      /<a href="(https:\/\/www\.youtube\.com\/watch\?v=([^"&]+))[^"]*"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="https://www.youtube.com/embed/$2" frameborder="0" allowfullscreen></iframe></div>'
                    )
                    ?.replace(
                      /<a href="(https:\/\/youtu\.be\/([^"?]+))[^"]*"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="https://www.youtube.com/embed/$2" frameborder="0" allowfullscreen></iframe></div>'
                    ) ?? '',
                  {
                    ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a','div','iframe','img','span','h1','h4'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'width', 'height', 'frameborder', 'allowfullscreen', 'class', 'alt']
                  }
                ),
              }}
            />
          </div>
        </Card>
      </div>

      {/* Styles pour le contenu de l'article */}
      <style>{`
        
        .article-content {
  color: #374151;
  line-height: 1.8;
  font-size: 1.1rem;
  word-wrap: normal;
  overflow-wrap: normal;
  white-space: normal;
  max-width: 100%;
}

.article-content .video-container {
  position: relative;
  width: 100%;
  margin: 1.5em 0;
}
.article-content .video-container iframe {
  border-radius: 0.75rem;
  width: 100%;
  height: 400px;
}

.article-content p {
  margin-bottom: 1em;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
        .dark .article-content {
          color: #e5e7eb;
        }
        .article-content h1 {
          font-size: 2em;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: #111827;
        }
        .dark .article-content h1 {
          color: #f9fafb;
        }
        .article-content h2 {
          font-size: 1.5em;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: #111827;
        }
        .dark .article-content h2 {
          color: #f9fafb;
        }
        .article-content h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin-top: 1.25em;
          margin-bottom: 0.5em;
          color: #111827;
        }
        .dark .article-content h3 {
          color: #f9fafb;
        }
        .article-content p {
          margin-bottom: 1em;
        }
        .article-content strong {
          font-weight: 700;
          color: #111827;
        }
        .dark .article-content strong {
          color: #f9fafb;
        }
        .article-content em {
          font-style: italic;
        }
        .article-content u {
          text-decoration: underline;
        }
        .article-content s {
          text-decoration: line-through;
        }
        .article-content blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin: 1.5em 0;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 0 0.75rem 0.75rem 0;
          padding: 1rem 1.5rem;
          font-style: italic;
          color: #1e40af;
        }
        .dark .article-content blockquote {
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
        }
        .article-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1em 0;
        }
        .article-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1em 0;
        }
        .article-content li {
          margin-bottom: 0.5em;
        }
        .article-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .article-content a:hover {
          color: #2563eb;
        }
        .article-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.75rem;
          margin: 1.5em 0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .article-content iframe,
        .article-content .ql-video {
          max-width: 100%;
          width: 100%;
          height: 400px;
          border-radius: 0.75rem;
          margin: 1.5em 0;
        }
        .article-content .ql-align-center {
          text-align: center;
        }
        .article-content .ql-align-right {
          text-align: right;
        }
        .article-content .ql-align-justify {
          text-align: justify;
        }
        .article-content pre {
          background: #1f2937;
          color: #e5e7eb;
          padding: 1rem;
          border-radius: 0.75rem;
          overflow-x: auto;
          margin: 1.5em 0;
        }
        .article-content code {
          background: #e5e7eb;
          padding: 0.2em 0.4em;
          border-radius: 0.25rem;
          font-size: 0.9em;
        }
        .dark .article-content code {
          background: #374151;
        }
        .article-content hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 2em 0;
        }
        .dark .article-content hr {
          border-top-color: #374151;
        }
      `}</style>
    </DashboardLayout>
  );
};

export default ArticleDetailPage;
